// Notifications (Phase 46): EVERY notification string. This is the one file Ali edits (D-10);
// nothing public is hard-coded in components, email templates or push payloads.
//
// NO IMPURE RUNTIME IMPORTS. `import type` from './notifications' is erased at compile time and
// keeps the copy keyed by the registry's own types so a new type cannot ship without copy. Phase 49
// adds runtime imports of './gamification' and './onboarding', both PURE and dependency-free apart
// from each other, so vitest (frontend, via `@shared/*`) and the backend email/push renderers (via
// the compiled `shared` package) still load this module with nothing impure attached. They are
// imported so the onboarding copy reads tier nudges and point values from their one source
// instead of hard-coding «٢٥» or «٧٠».
//
// Rules:
//  - public copy is Masri, one line, and NEVER contains an em dash or en dash (house rule for
//    public Arabic copy; frontend/src/lib/__tests__/notificationCopy.test.ts enforces it);
//  - admin copy is English;
//  - these are drafts from 46-UI-SPEC.md for Ali to rewrite (UI-SPEC assumption 6);
//  - numbers go through toLocaleString("ar-EG"); names are plain text, never HTML.

import type {
  NotificationPayload,
  NotificationType,
  PreferenceRowKey,
  WhatsappTemplateKey,
} from './notifications';
import { NEXT_TIER_NUDGE, POINT_VALUES } from './gamification';
import { FULL_REVIEW_POINTS } from './onboarding';

// ─── Helpers ───────────────────────────────────────────────────────────────────

export const toArabicNumber = (n: number): string => Number(n).toLocaleString("ar-EG");

const ANONYMOUS = "حد";
const STORE_FALLBACK = "المتجر";
const LOW_RATING_MAX = 2; // mirrors LOW_RATING_THRESHOLD in notifications.ts (no runtime import)

const nameOr = (name: string | undefined): string => (name && name.trim() ? name.trim() : ANONYMOUS);
const storeOf = (payload: NotificationPayload): string =>
  payload.storeName && payload.storeName.trim() ? payload.storeName.trim() : STORE_FALLBACK;

// D-07 / D-08: «أحمد» · «أحمد ومنى» · «أحمد و٤ آخرين». `distinctCount` is the number of distinct
// actors on the grouped row (the row keeps only the latest few names), defaulting to names.length.
export const buildActorList = (names: string[], distinctCount?: number): string => {
  const list = (names ?? []).map(nameOr);
  const count = Math.max(distinctCount ?? list.length, list.length);
  if (list.length === 0) return ANONYMOUS;
  if (count === 1) return list[0];
  if (count === 2 && list.length >= 2) return `${list[0]} و${list[1]}`;
  return `${list[0]} و${toArabicNumber(count - 1)} آخرين`;
};

// ─── Row titles, push titles, email subjects ───────────────────────────────────

export type TitleVars = {
  payload: NotificationPayload;
  actorNames: string[];
  actorCount: number;
  tierAr?: string;
};

type CopyEntry = {
  title: (v: TitleVars) => string;
  pushTitle?: string | ((v: TitleVars) => string);
  emailSubject?: (v: TitleVars) => string;
  emailCta?: string;
  metaLine?: (v: TitleVars) => string;
};

const isLowRating = (payload: NotificationPayload): boolean =>
  typeof payload.rating === "number" && payload.rating <= LOW_RATING_MAX;

const actorsOf = (v: TitleVars): string => buildActorList(v.actorNames, v.actorCount);
const isPlural = (v: TitleVars): boolean => Math.max(v.actorCount, v.actorNames.length) > 1;
const tierOf = (v: TitleVars): string => v.tierAr || v.payload.tierKey || "";

const DEFAULT_EMAIL_CTA = "شوف على يتفرج";

// Admin escalation item labels (English). Unknown types fall back to the raw key, humanised.
const ESCALATION_ITEM_LABELS: Record<string, string> = {
  review: "review",
  fb_post_review: "Facebook review",
  inquiry: "inquiry",
  claim: "claim",
  edit_request: "edit request",
  store_request: "store request",
  lead: "outreach lead",
  draft_store: "draft store",
  conversation: "chat",
};
const escalationLabel = (itemType?: string): string =>
  (itemType && ESCALATION_ITEM_LABELS[itemType]) || (itemType ? itemType.replace(/_/g, " ") : "item");

export const NOTIFICATION_COPY: Record<NotificationType, CopyEntry> = {
  claim_approved: {
    title: ({ payload }) => `مبروك! طلبك لإدارة ${storeOf(payload)} اتقبل`,
    pushTitle: "طلبك اتقبل",
    emailSubject: ({ payload }) => `اتقبل طلبك لإدارة ${storeOf(payload)}`,
    emailCta: "افتح لوحة المتجر",
  },
  claim_rejected: {
    title: ({ payload }) => `طلبك لإدارة ${storeOf(payload)} ماتقبلش`,
    pushTitle: ({ payload }) => `بخصوص طلبك لـ ${storeOf(payload)}`,
    emailSubject: ({ payload }) => `بخصوص طلبك لإدارة ${storeOf(payload)}`,
    emailCta: DEFAULT_EMAIL_CTA,
    metaLine: ({ payload }) => (payload.reason ? `السبب: ${payload.reason}` : ""),
  },
  store_review_received: {
    title: (v) =>
      isLowRating(v.payload)
        ? `${actorsOf(v)} ادّى ${storeOf(v.payload)} ${toArabicNumber(v.payload.rating ?? 0)} من ٥. ردّك ممكن يفرق`
        : `${actorsOf(v)} كتب تقييم جديد لـ ${storeOf(v.payload)}`,
    pushTitle: ({ payload }) => (isLowRating(payload) ? "تقييم محتاج ردّك" : `تقييم جديد لـ ${storeOf(payload)}`),
    emailSubject: ({ payload }) =>
      isLowRating(payload) ? `تقييم جديد لـ ${storeOf(payload)} محتاج ردّك` : `تقييم جديد لـ ${storeOf(payload)}`,
    emailCta: "شوف التقييم وردّ",
  },
  review_merchant_replied: {
    title: ({ payload }) => `${storeOf(payload)} ردّ على تقييمك`,
    pushTitle: ({ payload }) => `${storeOf(payload)} ردّ عليك`,
    emailSubject: ({ payload }) => `${storeOf(payload)} ردّ على تقييمك`,
    emailCta: DEFAULT_EMAIL_CTA,
  },
  review_commented: {
    title: (v) =>
      isPlural(v)
        ? `${actorsOf(v)} علّقوا على تقييمك لـ ${storeOf(v.payload)}`
        : `${actorsOf(v)} علّق على تقييمك لـ ${storeOf(v.payload)}`,
    // Used only when the user turns instant push on for this type; the default is the digest.
    pushTitle: "تعليق جديد على تقييمك",
  },
  review_liked: {
    title: (v) =>
      isPlural(v)
        ? `${actorsOf(v)} عجبهم تقييمك لـ ${storeOf(v.payload)}`
        : `${actorsOf(v)} عجبه تقييمك لـ ${storeOf(v.payload)}`,
  },
  tier_reached: {
    title: (v) => `مبروك! وصلت لمستوى ${tierOf(v)}`,
    pushTitle: "مستوى جديد",
    emailSubject: (v) => `وصلت لمستوى ${tierOf(v)} على يتفرج`,
    emailCta: DEFAULT_EMAIL_CTA,
  },
  points_awarded: {
    title: ({ payload }) => `كسبت ${toArabicNumber(payload.points ?? 0)} نقطة النهارده`,
  },
  escalation_opened: {
    title: ({ payload }) => `New escalation: ${escalationLabel(payload.itemType)}`,
    metaLine: ({ payload }) => payload.moderatorName ?? "",
  },
  // Phase 49 onboarding. Day 3/7 copy comes from the fact ladder (ONBOARDING_COPY.ladder below);
  // these functions only run at render time, so ONBOARDING_COPY being declared later is fine.
  // emailCta is static in CopyEntry; the onboarding email renderer uses ONBOARDING_COPY.ladder.cta.
  welcome: {
    title: () => "أهلاً بيك في يتفرج، حسابك جاهز",
    pushTitle: "حسابك جاهز",
    emailSubject: () => "حسابك على يتفرج جاهز",
    emailCta: "افتح صفحتك",
  },
  onboarding_day3: {
    title: ({ payload }) => ONBOARDING_COPY.ladder.line(payload),
    pushTitle: ({ payload }) => ONBOARDING_COPY.ladder.pushTitle(payload),
    emailSubject: ({ payload }) => ONBOARDING_COPY.ladder.line(payload),
    emailCta: "اكتب تقييمك",
  },
  onboarding_day7: {
    title: ({ payload }) => ONBOARDING_COPY.ladder.line(payload),
    pushTitle: ({ payload }) => ONBOARDING_COPY.ladder.pushTitle(payload),
    emailSubject: () => "أسبوعك الأول على يتفرج",
    emailCta: "اكتب تقييمك",
  },
};

// Push fallback title when a type has none.
export const PUSH_FALLBACK_TITLE = "يتفرج";

export const renderNotificationTitle = ({
  type,
  payload,
  actorNames,
  actorCount,
  tierAr,
}: {
  type: NotificationType;
  payload: NotificationPayload;
  actorNames: string[];
  actorCount: number;
  tierAr?: string;
}): string => NOTIFICATION_COPY[type].title({ payload: payload ?? {}, actorNames: actorNames ?? [], actorCount, tierAr });

// ─── Time and date groups ──────────────────────────────────────────────────────

const CAIRO_TZ = "Africa/Cairo";
const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const cairoDayKey = (d: Date): string => d.toLocaleDateString("en-CA", { timeZone: CAIRO_TZ });
// Whole Cairo calendar days between two instants (0 = same day, 1 = yesterday).
const cairoDaysAgo = (date: Date, now: Date): number => {
  const a = Date.parse(`${cairoDayKey(date)}T00:00:00Z`);
  const b = Date.parse(`${cairoDayKey(now)}T00:00:00Z`);
  return Math.round((b - a) / DAY);
};

export const formatNotificationTime = (date: Date | string, now: Date = new Date()): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const diff = now.getTime() - d.getTime();
  if (diff < MINUTE) return "دلوقتي";
  if (diff < HOUR) return `من ${toArabicNumber(Math.floor(diff / MINUTE))} دقيقة`;
  const daysAgo = cairoDaysAgo(d, now);
  if (daysAgo <= 0) return `من ${toArabicNumber(Math.floor(diff / HOUR))} ساعة`;
  if (daysAgo === 1) return "امبارح";
  return d.toLocaleDateString("ar-EG", { day: "numeric", month: "long", timeZone: CAIRO_TZ });
};

export const NOTIFICATION_DATE_GROUPS = {
  today: "النهارده",
  yesterday: "امبارح",
  thisWeek: "الأسبوع ده",
  older: "أقدم",
} as const;

export const groupLabelFor = (date: Date | string, now: Date = new Date()): string => {
  const d = typeof date === "string" ? new Date(date) : date;
  const daysAgo = cairoDaysAgo(d, now);
  if (daysAgo <= 0) return NOTIFICATION_DATE_GROUPS.today;
  if (daysAgo === 1) return NOTIFICATION_DATE_GROUPS.yesterday;
  if (daysAgo < 7) return NOTIFICATION_DATE_GROUPS.thisWeek;
  return NOTIFICATION_DATE_GROUPS.older;
};

// ─── Phone masking ─────────────────────────────────────────────────────────────

// Last 3 digits only, e.g. «•••• ١٢٣». Render inside dir="ltr".
export const maskPhone = (phone: string): string => {
  const digits = String(phone ?? "").replace(/\D/g, "");
  const tail = digits.slice(-3);
  const arabicTail = Array.from(tail)
    .map((c) => toArabicNumber(Number(c)))
    .join("");
  return `•••• ${arabicTail}`;
};

// ─── Bell and inbox chrome ─────────────────────────────────────────────────────

export const NOTIFICATION_CHROME = {
  bellAria: "التنبيهات",
  bellAriaWithCount: (n: number) => `التنبيهات، ${toArabicNumber(n)} جديد`,
  title: "التنبيهات",
  markAllRead: "علّم الكل كمقروء",
  seeAll: "شوف كل التنبيهات",
  settingsLink: "إعدادات التنبيهات",
  loadMore: "اعرض أقدم",
  retentionFootnote: "بنحتفظ بالتنبيهات ٩٠ يوم بس.",
  markAllFailedToast: "مقدرناش نعلّمهم كمقروء. جرّب تاني.",
  focusChip: "من تنبيهاتك",
  focusNotFoundToast: "التقييم ده مبقاش موجود",
  emptyHeading: "مفيش تنبيهات لسه",
  emptyBody: "لما حد يرد أو يعلّق على تقييمك هتلاقيه هنا.",
  emptyCta: "اكتب تقييم",
  error: "مقدرناش نجيب التنبيهات. جرّب تاني كمان شوية.",
  retry: "جرّب تاني",
  badgeOverflow: "+٩",
} as const;

// ─── Preferences matrix ────────────────────────────────────────────────────────

const PREFERENCE_ROW_LABELS: Record<PreferenceRowKey, string> = {
  account: "الترحيب ورسايل أول أسبوع",
  claim_decision: "قرار طلب إدارة المتجر",
  store_review: "تقييم جديد على متجرك",
  merchant_reply: "المتجر ردّ على تقييمك",
  comment: "حد علّق على تقييمك",
  like: "حد عجبه تقييمك",
  tier: "وصلت لمستوى جديد",
  points: "نقاط جديدة",
};

export const PREFERENCES_COPY = {
  sectionTitle: "التنبيهات",
  sectionSubtitle: "اختار إيه يوصلك وفين.",
  columns: {
    inApp: "في الموقع",
    email: "الإيميل",
    whatsapp: "واتساب",
    push: "إشعارات المتصفح",
  },
  groups: {
    account: "حسابك",
    merchant: "متجرك",
    reviews: "تقييماتك",
    rewards: "المكافآت",
  },
  rows: PREFERENCE_ROW_LABELS,
  digestHint: "في الملخص اليومي",
  email: {
    verified: (email: string) => `على ${email}`,
    unverified: "أكّد إيميلك الأول",
    sendVerify: "ابعت لينك التأكيد",
    verifySent: (email: string) => `بعتنالك لينك على ${email}`,
    none: "مفيش إيميل على حسابك",
  },
  whatsapp: {
    consented: (phoneMasked: string) => `على رقم ${phoneMasked}`,
    revoke: "اقفل واتساب",
    off: "واتساب مقفول",
    enable: "فعّل واتساب",
    noPhone: "محتاج رقم موبايل متأكد منه",
  },
  push: {
    subscribed: "مفعّلة على الجهاز ده",
    defaultPermission: "مقفولة على الجهاز ده",
    enableHere: "فعّل على الجهاز ده",
    denied: "مقفولة من المتصفح",
    unsupported: "المتصفح ده مش بيدعمها",
    iosNotInstalled: "ضيف الموقع للشاشة الرئيسية الأول",
    deniedHelp: "الإشعارات مقفولة من المتصفح. تقدر تفتحها من إعدادات الموقع في المتصفح.",
  },
  saveFailedToast: "مقدرناش نحفظ التغيير. جرّب تاني.",
  revokeWhatsappToast: "تمام، مش هنبعتلك على واتساب تاني.",
} as const;

// ─── WhatsApp opt-in (D-16) ────────────────────────────────────────────────────

export const WHATSAPP_OPTIN_COPY = {
  checkboxLabel: "ابعتلي تنبيهات على واتساب",
  helperLogin: "على نفس الرقم ده، وللحاجات المهمة بس. تقدر تقفلها من الإعدادات.",
  helperClaim: (phoneMasked: string) => `هتوصل على رقم المتجر ${phoneMasked}، وتقدر تقفلها من الإعدادات.`,
  cardTitle: "عايز تنبيهاتك توصلك على واتساب؟",
  cardBody: (phoneMasked: string) => `هنبعتلك الحاجات المهمة بس على رقم ${phoneMasked}.`,
  accept: "أيوه، فعّلها",
  dismiss: "مش دلوقتي",
  successToast: "تمام، التنبيهات المهمة هتوصلك على واتساب",
  // Phase 49 onboarding variant (S2 finish screen, S3 second ask). Only this variant carries the
  // D-13 first-week promise, because only it records consent for the first-week sends.
  onboardingTitle: "ابعتلي على واتساب",
  onboardingValue: "هنبعتلك لما حد يرد على تقييمك أو يعجبه، ورسالتين بالكتير في أول أسبوع.",
  onboardingFootnote: (phoneMasked: string) => `على رقم ${phoneMasked}، وتقدر تقفلها من الإعدادات في أي وقت.`,
  onboardingAlreadyOn: (phoneMasked: string) => `واتساب متفعّل على رقم ${phoneMasked}.`,
  onboardingCardTitle: "عايز تعرف لما حد يرد على تقييمك؟",
  onboardingCardBody: (phoneMasked: string) =>
    `هنبعتلك على واتساب على رقم ${phoneMasked} لما حد يرد على تقييمك أو يعجبه، ورسالتين بالكتير في أول أسبوع.`,
} as const;

// ─── Push soft ask (D-17) ──────────────────────────────────────────────────────

export const PUSH_SOFT_ASK_COPY = {
  surfaces: {
    review_thank_you: {
      title: "عايز تعرف لما المتجر يرد عليك؟",
      body: "فعّل الإشعارات وهنقولك أول ما يحصل.",
    },
    claim_submitted: {
      title: "عايز تعرف أول ما نراجع طلبك؟",
      body: "فعّل الإشعارات وهنقولك على طول.",
    },
    bell_first_open: {
      title: "خلّي التنبيهات توصلك حتى لو الموقع مقفول",
      body: "هنبعت الحاجات المهمة بس.",
    },
    ios_install: {
      title: "على الآيفون، ضيف يتفرج للشاشة الرئيسية الأول",
      body: "من زرار المشاركة اختار «إضافة إلى الشاشة الرئيسية»، وبعدها فعّل الإشعارات.",
      button: "فهمت، هضيفه",
    },
  },
  accept: "فعّل الإشعارات",
  dismiss: "مش دلوقتي",
  grantedToast: "تمام، الإشعارات اتفعّلت",
  deniedLine: "تمام، ممكن تفعّلها بعدين من الإعدادات.",
} as const;

// ─── Email verify, unsubscribe, footer ─────────────────────────────────────────

export const EMAIL_PAGES_COPY = {
  verifySubject: "أكّد إيميلك على يتفرج",
  verifyCta: "أكّد الإيميل",
  verifyLoading: "بنأكّد إيميلك...",
  verifySuccess: "تمام، إيميلك اتأكّد",
  verifyExpired: "اللينك ده خلص. ابعت واحد جديد من الإعدادات.",
  backToSettings: "ارجع للإعدادات",
  unsubscribeQuestionType: (typeLabel: string) => `عايز توقف إيميلات «${typeLabel}»؟`,
  unsubscribeQuestionAll: "عايز توقف كل إيميلات يتفرج؟",
  unsubscribeButton: "أيوه، وقّفها",
  unsubscribeDone: "خلاص، مش هتوصلك الإيميلات دي تاني. تقدر ترجعها من إعدادات التنبيهات.",
  unsubscribeInvalid: "اللينك ده مش شغال. تقدر تغيّر إعداداتك من صفحة حسابك.",
  footerUnsubscribe: "مش عايز الإيميلات دي؟ وقّفها",
  footerSettings: "غيّر إعدادات التنبيهات",
} as const;

// ─── Daily digest (email always, one push summary, never WhatsApp) ─────────────

export const DIGEST_COPY = {
  subject: "ملخص يومك على يتفرج",
  likesLine: (n: number) => `${toArabicNumber(n)} حد عجبهم تقييماتك`,
  commentsLine: (n: number) => `${toArabicNumber(n)} تعليق جديد على تقييماتك`,
  pointsLine: (points: number) => `كسبت ${toArabicNumber(points)} نقطة`,
  pushTitle: "ملخص النهارده",
  pushBody: (n: number) => `عندك ${toArabicNumber(n)} تنبيه جديد`,
} as const;

// ─── Admin (English, LTR) ──────────────────────────────────────────────────────

export const ADMIN_NOTIFICATION_COPY = {
  bellTitle: "Notifications",
  bellAriaWithCount: (n: number) => `Notifications, ${n} new`,
  rowMeta: ({ moderatorName, relativeTime }: { moderatorName?: string; relativeTime: string }) =>
    moderatorName ? `${moderatorName} · ${relativeTime}` : relativeTime,
  markAllRead: "Mark all as read",
  emptyHeading: "No notifications yet",
  emptyBody: "Escalations opened by moderators show up here.",
  error: "Couldn't load notifications.",
  retry: "Reload notifications",
  openEscalations: "Open escalations",
  deliveryLog: "Delivery log",
  testSent: (channel: string) => `Test ${channel} sent to you`,
  testFailed: ({ channel, error }: { channel: string; error: string }) => `Test ${channel} failed: ${error}`,
  saved: "Notification settings saved",
  saveFailed: (error: string) => `Couldn't save settings: ${error}`,
} as const;

// ─── WhatsApp Meta templates (D-10, submitted in plan 46-27) ───────────────────

// Bodies Ali submits to Meta as UTILITY templates. Placeholders {{1}}..{{n}} are in EXACTLY the
// order buildWhatsappBodyParams emits (claim_* and review_merchant_replied: {{1}} store name;
// store_review_received(_low): {{1}} actor, {{2}} store, {{3}} rating). A body never starts or ends
// with a variable and has no newlines (Meta error 132018, RESEARCH A3).
//
// The URL button base is https://www.yetfarrag.com/{{1}}; the button suffix is the notification's
// deepLink (without withNotificationSource tagging) minus its leading '/'.
export type WhatsappTemplateCopy = {
  category: "UTILITY";
  language: "ar";
  body: string;
  buttonText: string;
  sampleParams: string[];
};

export const WHATSAPP_TEMPLATE_COPY: Record<WhatsappTemplateKey, WhatsappTemplateCopy> = {
  claim_approved: {
    category: "UTILITY",
    language: "ar",
    body: "مبروك! طلبك لإدارة {{1}} على يتفرج اتقبل، وتقدر تفتح لوحة المتجر دلوقتي.",
    buttonText: "افتح",
    sampleParams: ["متجر النيل"],
  },
  claim_rejected: {
    category: "UTILITY",
    language: "ar",
    body: "بخصوص طلبك لإدارة {{1}} على يتفرج: الطلب ماتقبلش المرة دي، والسبب موجود في لوحة المتجر.",
    buttonText: "افتح",
    sampleParams: ["متجر النيل"],
  },
  store_review_received: {
    category: "UTILITY",
    language: "ar",
    body: "وصل تقييم جديد من {{1}} لـ {{2}} على يتفرج، {{3}} من ٥. تقدر تشوفه وتردّ عليه.",
    buttonText: "شوف التقييم",
    sampleParams: ["أحمد", "متجر النيل", "5"],
  },
  store_review_received_low: {
    category: "UTILITY",
    language: "ar",
    body: "وصل تقييم من {{1}} لـ {{2}} على يتفرج، {{3}} من ٥. ردّك ممكن يفرق.",
    buttonText: "شوف التقييم",
    sampleParams: ["أحمد", "متجر النيل", "2"],
  },
  review_merchant_replied: {
    category: "UTILITY",
    language: "ar",
    body: "متجر {{1}} ردّ على تقييمك على يتفرج، تقدر تشوف الرد دلوقتي.",
    buttonText: "شوف التقييم",
    sampleParams: ["متجر النيل"],
  },
  // Phase 49 (D-24, UI-SPEC S7). account_created {{1}}: first name (no fallback; the channel skips
  // the send when it is empty). onboarding_in_week {{1}}: one sentence, ONBOARDING_COPY.waInWeek.
  account_created: {
    category: "UTILITY",
    language: "ar",
    body: "أهلاً {{1}}، حسابك على يتفرج اتعمل على الرقم ده، وهنبعتلك هنا لما حد يرد على تقييمك أو يعجبه. تقدر تقفل الرسايل دي من الإعدادات.",
    buttonText: "افتح حسابك",
    sampleParams: ["أحمد"],
  },
  onboarding_in_week: {
    category: "UTILITY",
    language: "ar",
    body: "أهلاً من يتفرج. {{1}}، وتقدر تشوف التفاصيل من الزرار.",
    buttonText: "شوف التفاصيل",
    sampleParams: ["٣ ناس قالوا إن تقييمك لـ متجر النيل مفيد"],
  },
};

// ─── Onboarding, first 7 days (Phase 49) ───────────────────────────────────────

// Every Arabic onboarding string from 49-UI-SPEC: welcome card (S1), finish screen (S2), return
// banner (S4), emails (S5), the fact ladder (S5/S6) and the WhatsApp in-week sentence (S7).
// Drafts for Ali to edit; no em dash or en dash.

const STORE_NAME_WA_MAX = 24;

// WhatsApp params cap at 60 characters (sanitizeTemplateParam), so a long store name would cut the
// sentence's ending off. Names longer than `max` code points become the first `max` plus «…».
export const truncateStoreName = (name: string, max: number = STORE_NAME_WA_MAX): string => {
  const chars = Array.from(String(name ?? "").trim());
  return chars.length > max ? `${chars.slice(0, max).join("")}…` : chars.join("");
};

// Count words (UI-SPEC fact ladder): 1 «واحد», 2 «اتنين», 3 to 10 «{n} ناس», 11+ «{n} واحد».
const countWord = (n: number): string => {
  if (n === 1) return "واحد";
  if (n === 2) return "اتنين";
  if (n <= 10) return `${toArabicNumber(n)} ناس`;
  return `${toArabicNumber(n)} واحد`;
};

type LadderKind = "likes" | "comments" | "reply" | "tier" | "reviewer" | "nonReviewer" | "none";

const ladderKind = (p: NotificationPayload): LadderKind => {
  switch (p.ladderRung) {
    case 1:
      return (p.reactions ?? 0) > 0 ? "likes" : "comments";
    case 2:
      return "reply";
    case 3:
      return "tier";
    case 4:
      return p.hasReviewed ? "reviewer" : "nonReviewer";
    default:
      return "none";
  }
};

const ladderLineFor = (p: NotificationPayload, store: string): string => {
  switch (ladderKind(p)) {
    case "likes": {
      const n = p.reactions ?? 0;
      if (n === 1) return `واحد قال إن تقييمك لـ ${store} مفيد`;
      return `${countWord(n)} قالوا إن تقييمك لـ ${store} مفيد`;
    }
    case "comments": {
      const n = p.commentCount ?? 0;
      if (n <= 1) return `فيه تعليق جديد على تقييمك لـ ${store}`;
      return `فيه ${toArabicNumber(n)} ${n >= 11 ? "تعليق" : "تعليقات"} على تقييمك لـ ${store}`;
    }
    case "reply":
      return `صاحب ${store} ردّ على تقييمك`;
    case "tier":
      return NEXT_TIER_NUDGE.oneReviewAway({ tierAr: p.tierAr ?? "" });
    case "reviewer":
      return `لو اشتريت من ${store} قبل كده، تقييمك يفرق مع اللي بعدك`;
    case "nonReviewer":
      return `تقييمك لـ ${store} لسه مستنيك`;
    case "none":
      // No rung resolved: the backend skips the send (never an empty message).
      return "";
  }
};

const LADDER_PUSH_TITLES: Record<LadderKind, string> = {
  likes: "تقييمك فاد ناس",
  comments: "تعليق على تقييمك",
  reply: "ردّ على تقييمك",
  tier: "قربت من مستوى جديد",
  reviewer: "متجر مستني تقييمك",
  nonReviewer: "متجر مستني تقييمك",
  none: "",
};

const LADDER_CTAS: Record<LadderKind, string> = {
  likes: "شوف تقييمك",
  comments: "شوف التعليقات",
  reply: "شوف الرد",
  tier: "قيّم متجر",
  reviewer: "اكتب تقييمك",
  nonReviewer: "اكتب تقييمك",
  none: "",
};

const pointsLine = ({ points, tierAr }: { points: number; tierAr: string }): string =>
  `معاك ${toArabicNumber(points)} نقطة، ومستواك دلوقتي ${tierAr}.`;

const hasName = (name: string | undefined): name is string => Boolean(name && name.trim());

export const ONBOARDING_COPY = {
  countWord,

  // ─── Fact ladder (first real nonzero value wins, D-16) ───
  ladder: {
    // In-app title, email subject + title, push body. Email and push keep the FULL store name.
    line: (p: NotificationPayload): string => ladderLineFor(p, storeOf(p)),
    pushTitle: (p: NotificationPayload): string => LADDER_PUSH_TITLES[ladderKind(p)],
    cta: (p: NotificationPayload): string => LADDER_CTAS[ladderKind(p)],
    // Second body line when another rung is also nonzero (rung 1 likes + comments, or rung 1 + 2).
    secondFact: (p: NotificationPayload): string => {
      if (p.ladderRung !== 1) return "";
      const comments = p.commentCount ?? 0;
      if (ladderKind(p) === "likes" && comments > 0) {
        return comments === 1
          ? "وفيه تعليق على نفس التقييم."
          : `وفيه ${toArabicNumber(comments)} تعليق على نفس التقييم.`;
      }
      if (p.merchantReplied) return `صاحب ${storeOf(p)} ردّ على تقييمك.`;
      return "";
    },
    // Why this store (rungs 3 and 4). Empty when the reason's variable is missing.
    contextLine: (p: NotificationPayload): string => {
      switch (p.pickReason) {
        case "signup_store":
          return "ده المتجر اللي كنت بتتفرج عليه لما عملت حسابك.";
        case "last_viewed":
          return "ده آخر متجر اتفرجت عليه عندنا.";
        case "popular":
          return hasName(p.categoryName)
            ? `ده من أكتر المتاجر اللي الناس بتدوّر عليها في ${p.categoryName.trim()}.`
            : "";
        case "same_category":
          return hasName(p.contextStoreName) ? `اخترناه من نفس فئة ${p.contextStoreName.trim()} اللي قيّمته.` : "";
        default:
          return "";
      }
    },
  },

  // ─── WhatsApp onboarding_in_week {{1}} (UI-SPEC S7) ───
  // Store names are truncated to 24 + «…» so both variants stay within the 60-character param cap.
  waInWeek: {
    variantA: (p: NotificationPayload): string => ladderLineFor(p, truncateStoreName(storeOf(p))),
    variantB: ({ storeName }: { storeName: string }): string =>
      `لقينا متجر ممكن يعجبك اسمه ${truncateStoreName(storeName || STORE_FALLBACK)}`,
  },

  // ─── Emails (S5) ───
  email: {
    pointsLine,
    welcome: {
      subject: "حسابك على يتفرج جاهز",
      title: (firstName?: string): string =>
        hasName(firstName) ? `أهلاً يا ${firstName.trim()}، حسابك جاهز` : "أهلاً بيك، حسابك على يتفرج جاهز",
      reviewPublished: (storeName: string): string =>
        `تقييمك لـ ${storeName || STORE_FALLBACK} اتنشر، والناس تقدر تشوفه دلوقتي.`,
      firstReview: `أول تقييم ليك بيجيبلك لحد ${toArabicNumber(FULL_REVIEW_POINTS)} نقطة.`,
      cta: "افتح صفحتك",
      rateKnownStore: "قيّم متجر تعرفه",
      rateAnotherStore: "قيّم متجر تاني",
    },
    verifyWelcome: {
      subject: "أهلاً بيك في يتفرج، أكّد إيميلك",
      title: (firstName?: string): string =>
        hasName(firstName) ? `أهلاً يا ${firstName.trim()}، فاضل خطوة واحدة` : "أهلاً بيك، فاضل خطوة واحدة",
      line1: "أكّد إيميلك عشان نقدر نبعتلك لما حد يرد على تقييمك أو يعجبه.",
      line2: (points: number): string =>
        `حسابك فيه ${toArabicNumber(points)} نقطة من دلوقتي، وأول تقييم بيجيبلك لحد ${toArabicNumber(FULL_REVIEW_POINTS)} نقطة كمان.`,
      cta: EMAIL_PAGES_COPY.verifyCta,
      footnote: "اللينك ده شغال ٢٤ ساعة.",
    },
    day7: {
      subject: "أسبوعك الأول على يتفرج",
      footnote: "دي آخر رسالة ترحيب هتوصلك مننا.",
    },
  },

  // ─── Welcome card (S1) ───
  welcomeCard: {
    // UI-SPEC defines n = 3 and n = 2 only; the card never shows fewer than two items.
    title: (n: number): string =>
      n >= 3
        ? `أهلاً بيك في يتفرج! ${toArabicNumber(n)} حاجات تبدأ بيها`
        : "أهلاً بيك في يتفرج! حاجتين تبدأ بيهم",
    progress: (done: number, total: number): string =>
      `خلّصت ${toArabicNumber(done)} من ${toArabicNumber(total)}`,
    dismissAria: "إخفاء",
    doneBadgeAria: "اتعمل",
    allDone: "كده خلّصت البداية، نورتنا!",
    item1: {
      title: "قيّم متجر اشتريت منه قبل كده",
      helper: "اكتب اسم المتجر وهنفتحلك صفحة التقييم على طول.",
      done: "تمام، تقييمك اتنشر.",
      placeholder: "اكتب اسم المتجر…",
      aria: "دوّر على متجر تقيّمه",
    },
    item2a: {
      title: `كمّل ملفك الشخصي وخد ${toArabicNumber(POINT_VALUES.profile_completed)} نقطة`,
      helper: "اسمك وصورتك ومدينتك، عشان الناس تعرف مين اللي بيقيّم.",
      button: "كمّل ملفك",
    },
    item2b: {
      title: `ضيف مدينتك وخد ${toArabicNumber(POINT_VALUES.profile_completed)} نقطة`,
      helper: "خطوة واحدة وملفك يبقى كامل.",
      button: "ضيف مدينتك",
    },
    item2c: {
      title: `ضيف صورتك وخد ${toArabicNumber(POINT_VALUES.profile_completed)} نقطة`,
      helper: "خطوة واحدة وملفك يبقى كامل.",
      button: "ضيف صورة",
    },
    item2Done: `ملفك كامل، والـ${toArabicNumber(POINT_VALUES.profile_completed)} نقطة اتضافت.`,
    item3a: {
      title: "خلّي التنبيهات توصلك على واتساب",
      helper: "هنبعتلك لما حد يرد على تقييمك أو يعجبه، ورسالتين بالكتير في أول أسبوع.",
      button: "فعّل واتساب",
      done: "اتفعّل. هنبعتلك على واتساب.",
    },
    item3b: {
      title: "فعّل إشعارات المتصفح",
      helper: "هنقولك لما حد يرد على تقييمك أو يعجبه، وكام تنبيه بسيط في أول أسبوع.",
      button: "فعّل الإشعارات",
      done: "اتفعّلت على الجهاز ده.",
    },
    item3AlreadyOn: "اتفعّلت.",
    heroEyebrow: "أهلاً بيك · دليلك للمتاجر الأونلاين في مصر",
  },

  // ─── Phone "finish your account" screen (S2) ───
  finish: {
    title: "خلّص حسابك",
    description: "فاضلك خطوة واحدة وتبدأ.",
    verifiedAlert: "تمام، رقمك اتأكّد.",
    nameLabel: "اسمك",
    namePlaceholder: "اسمك زي ما تحب الناس تشوفه",
    nameHelper: "هيظهر على ملفك وعلى تقييماتك.",
    nameRequired: "اكتب اسمك الأول",
    nameTooShort: "الاسم لازم يكون حرفين على الأقل",
    submit: "احفظ وكمّل",
    submitting: "بنحفظ…",
    nameSaveFailed: "مقدرناش نحفظ اسمك. جرّب تاني.",
    consentFailedToast: "مقدرناش نفعّل واتساب دلوقتي. تقدر تفعّله من الإعدادات.",
  },

  // ─── Return banner (S4, D-25 transparency) ───
  returnBanner: {
    title: "بصراحة، وحشتنا",
    body: "إحنا والناس اللي بتكتب تقييمات هنا كان نفسنا نشوفك تاني، عشان كده بعتنالك المتجر ده. لو جرّبته قبل كده، تقييمك هيفرق مع اللي بعدك.",
    cta: "اكتب تقييمك",
    dismiss: WHATSAPP_OPTIN_COPY.dismiss,
    closeAria: "إخفاء",
  },
} as const;

